import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useServices from "../hooks/useServices";
import { CURRENCY_NOTE, registerByEmailHref } from "../data/practice";
import { BUNDLES, REGULAR_SESSION_PRICE, bundlePerSession } from "../data/offerings";
import jasminePortrait from "../assets/jasmine-red-backdrop.jpg";
import { MessageSquare, FileText, User, Palette, Clock, Brush, Heart } from "lucide-react";
import SchedulingInfo from "../components/SchedulingInfo";
import CrisisNotice from "../components/CrisisNotice";
import RequestCallForm from "../components/RequestCallForm";
import { sendContactForm } from "../services/api/contactService";
import config from "../config";

const CONTACT_EMAIL = config.business.contactEmail;
const MESSAGE_MIN_LENGTH = 10;

const EMPTY_CONTACT_FORM = {
  name: '',
  email: '',
  message: '',
  website: '' // honeypot: hidden from people, bots tend to fill it
};

const ICONS = {
  "intake-session": FileText,
  "individual-talk-therapy": User,
  "individual-art-therapy": Palette,
  "extended-talk-therapy": Clock,
  "extended-individual-art-therapy": Brush,
  "safe-space-session": Heart
};

/** "$65 a session" when exact, else "About $88 a session" (price over sessions, intake included). */
function perSessionText(bundle) {
  const each = bundlePerSession(bundle);
  return each * bundle.sessions === bundle.price ? `$${each} a session` : `About $${each} a session`;
}

/**
 * Her four bundles beside the services: name, price, what's included and
 * the price of each session next to a regular session (no "save" claims).
 * Bought by email on /packages.
 */
function BundlesSummary() {
  return (
    <section className="home-bundles" aria-labelledby="home-bundles-heading">
      <h3 id="home-bundles-heading">Therapy bundles</h3>
      <p className="muted">
        A regular session is ${REGULAR_SESSION_PRICE}. Bundles bring the price of each session down. They are purchased by email.
      </p>
      <ul className="home-bundle-list">
        {BUNDLES.map((b) => (
          <li key={b.id} className="home-bundle">
            <h4>{b.title}</h4>
            <p className="home-bundle-price">${b.price}</p>
            <p>{b.includes}</p>
            <p className="home-bundle-each">
              {perSessionText(b)} <span className="muted">(regular session ${REGULAR_SESSION_PRICE})</span>
            </p>
          </li>
        ))}
      </ul>
      <Link className="read-more-link home-bundles-link" to="/packages">
        See bundle details and student pricing <span aria-hidden="true">→</span>
      </Link>
    </section>
  );
}

function Leaf({ className, size = 60 }) {
  return (
    <svg className={`leaf ${className||""}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden>
      <path d="M50 10C30 20 15 40 10 60c-3 12 0 20 10 20 25 0 52-13 66-38 8-14-2-36-36-32Z" />
    </svg>
  );
}

export default function Home() {
  const revealRefs = useRef([]);
  const navigate = useNavigate();
  // Static copy merged with the backend's price, duration and bookable_online
  // (backend wins), so a service the owner takes off online booking shows
  // "Email to Register" here as it does on its detail page.
  const { services } = useServices();

  // Contact form state
  const [contactFormData, setContactFormData] = useState(EMPTY_CONTACT_FORM);
  const [contactFormLoading, setContactFormLoading] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.15 }
    );
    revealRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRevealRef = el => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };
  const goCheckout = (slug) => navigate(`/checkout?service=${encodeURIComponent(slug)}`);

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!contactFormData.name.trim() || !contactFormData.email.trim() || !contactFormData.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (contactFormData.message.trim().length < MESSAGE_MIN_LENGTH) {
      toast.error(`Please write at least ${MESSAGE_MIN_LENGTH} characters in your message.`);
      return;
    }

    setContactFormLoading(true);

    try {
      await sendContactForm(contactFormData);
      toast.success("Thanks! I will reach out shortly.");

      // Reset form
      setContactFormData(EMPTY_CONTACT_FORM);
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error(error.message || "Failed to send message. Please try again.");
    } finally {
      setContactFormLoading(false);
    }
  };

  // Handle input change
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (<>
    <header id="home" className="hero">
      <Leaf className="leaf1" size={60} />
      <Leaf className="leaf2" size={55} />
      <Leaf className="leaf3" size={70} />
      <div className="container">
        {/* Her welcome text (November 2026 corrections), word for word. */}
        <h1>Welcome to Healing Hues</h1>
        <p className="hero-tagline"><em>Creative care, held for everyone.</em></p>
        <p>
          Healing Hues is a therapeutic practice dedicated to helping individuals better understand their emotions, reconnect with themselves, and build tools to navigate life’s experiences. Through art therapy, talk therapy, and creative mental health programming, I create spaces for emotional exploration, embodied awareness, self-discovery, and meaningful connection.
        </p>
        <p>
          Services are available online and in person, including home-based sessions where appropriate, with opportunities for individuals, families, and communities to engage in creative care.
        </p>
        <p>Healing happens in color. Let’s create something meaningful together.</p>
        <div style={{ marginTop: "1rem" }}>
          <Link className="btn-cta" to="/checkout">Start Your Journey</Link>
        </div>
      </div>
    </header>

          {/* ABOUT */}
      <section id="about" className="section white">
        <div className="container reveal" ref={addRevealRef}>
          <div className="about">
            <img
              src={jasminePortrait}
              alt="Jasmine G. Smith"
              width={1066}
              height={1600}
              loading="lazy"
              decoding="async"
              style={{
                maxWidth: "100%",
                height: "auto",
                display: "block",
                borderRadius: "1rem",
                aspectRatio: "4 / 5",
                objectFit: "cover",
                objectPosition: "50% 15%"
              }}
            />
            <div className="copy">
              <h2>About Healing Hues</h2>
              <p className="muted">Meet Jasmine G. Smith, MS, ATR-P, LPC, NCC</p>
              <p>
                Hi, I’m Jasmine, a Licensed Professional Counselor and Art Therapist-Provisional based in Nassau, Bahamas. I created Healing Hues because I believe that healing doesn’t have to look one way. Sometimes it looks like talking. Sometimes it looks like creating. And sometimes it looks like both at once.
              </p>
              <p>
                My work is rooted in the belief that every person deserves a space to feel seen, heard, and supported, no matter where they are on their journey. Whether you’re navigating something heavy, looking to understand yourself better, or simply ready to try something different, I’m here for it.
              </p>
              <p>
                At Healing Hues, you’ll find individual art therapy and talk therapy, in-home sessions, workshops, and free art therapy groups for adults, teens, and families, because I believe access to mental health support shouldn’t always come with a price tag. I also work with schools, community organizations, and families across The Bahamas to bring creative wellness programming directly into the community.
              </p>
              <p>
                All services are provided under the clinical supervision of Dr. Jessica Stallings, ATR-BC, LIMHP, LPC, LMHC.
              </p>
              <p>
                Welcome to Healing Hues. I’m glad you’re here.
              </p>
              <div style={{ marginTop: "1rem" }}>
                <Link
                  to="/about-more"
                  className="read-more-link"
                  style={{
                    display: "inline-block",
                    color: "#4c3f63",
                    textDecoration: "underline",
                    fontWeight: "bold",
                    cursor: "pointer"
                  }}
                >
                  Read more about Jasmine and Healing Hues <span aria-hidden="true">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

    <section id="services" className="section">
      <div className="container reveal" ref={addRevealRef}>
        <h2>Services</h2>
        <p className="muted">Choose the support that fits you best.</p>
        <p className="muted currency-note">{CURRENCY_NOTE}</p>
        <SchedulingInfo />

        

        <div className="services" style={{ marginTop: "1rem" }}>
          {services.map(s => {
            const Icon = ICONS[s.slug] || MessageSquare;
            return (
              <div className="card" key={s.slug} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <Icon className="icon" aria-hidden />
              <h3>{s.title}</h3>
              <p>{s.short}</p>
              <div style={{ marginTop: "auto" }}>
                <p className="muted" style={{ marginBottom: "1rem", textAlign: "left" }}>
                {s.duration} • ${s.price}
                </p>
                <Link
                to={`/services/${s.slug}`}
                className="read-more-link"
                style={{
                  color: "#4c3f63",
                  textDecoration: "underline",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "block",
                  marginBottom: ".5rem"
                }}
                >
                Read More
                </Link>
                {s.bookableOnline === false ? (
                <a
                className="btn-cta"
                href={registerByEmailHref(s.title)}
                style={{
                  width: "50%",
                  minWidth: "100px",
                  marginTop: "2rem",
                  textAlign: "left",
                  display: "block"
                }}
                >
                Email to Register
                </a>
                ) : (
                <button
                type="button"
                className="btn-cta"
                style={{
                  width: "50%",
                  minWidth: "100px",
                  marginTop: "2rem",
                  textAlign: "left",
                  display: "block"
                }}
                onClick={() => goCheckout(s.slug)}
                >
                Book Now
                </button>
                )}
              </div>
              </div>
            );
          })}
        </div>

        <BundlesSummary />

        <RequestCallForm headingLevel={3} />
      </div>
    </section>

    <section id="contact" className="section white wc-bg">
      <div className="container reveal" ref={addRevealRef}>
        <h2>Get in touch</h2>
        <p className="muted">Have questions or ready to book? Send a message.</p>
        <div className="contact" style={{ marginTop: "1rem" }}>
          <form onSubmit={handleContactSubmit}>
            <label>
              Full Name
              <input
                type="text"
                name="name"
                placeholder="Jane Doe"
                value={contactFormData.name}
                onChange={handleContactInputChange}
                required
                disabled={contactFormLoading}
              />
            </label>
            <label>
              Email
              <input
                type="email"
                name="email"
                placeholder="you@example.com"
                value={contactFormData.email}
                onChange={handleContactInputChange}
                required
                disabled={contactFormLoading}
              />
            </label>
            <label>
              Message
              <textarea
                name="message"
                placeholder="How can I help?"
                value={contactFormData.message}
                onChange={handleContactInputChange}
                required
                minLength={MESSAGE_MIN_LENGTH}
                aria-describedby="contact-message-hint"
                disabled={contactFormLoading}
              />
            </label>
            <p id="contact-message-hint" className="field-hint">
              Please write at least {MESSAGE_MIN_LENGTH} characters.
            </p>
            {/* Honeypot: visually hidden and skipped by keyboard/screen readers */}
            <div className="hp-field" aria-hidden="true">
              <label>
                Website
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={contactFormData.website}
                  onChange={handleContactInputChange}
                />
              </label>
            </div>
            <button
              className="btn-cta"
              type="submit"
              disabled={contactFormLoading}
            >
              {contactFormLoading ? "Sending..." : "Send Message"}
            </button>
            <CrisisNotice variant="form" />
          </form>
          <div>
            <div className="card">
              <h3>Location</h3>
              <p>In person: in-home sessions in Nassau</p>
              <p>Virtually: all over The Bahamas</p>
              <h3 style={{marginTop:"1rem"}}>Email</h3>
              <p><a className="text-link" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </>);
}
